import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axiosPostV3 } from '../../api/axios-handler';
import { useFi } from '../../contexts/fi-context';
import { usePeersona } from '../../contexts/peersona-context';
import { Resource, useResources } from './useResources';
import useAuth from '../utils/useAuth';
import { useSessionStorage } from '../utils/useSessionStorage';

/** Max tiles returned / shown in the organizational resources drawer. */
export const ORGANIZATIONAL_RESOURCES_MAX = 5;
export const ORGANIZATIONAL_RESOURCES_VISIBLE = 4;

/** @deprecated Prefer ORGANIZATIONAL_RESOURCES_MAX — kept for Peersona call sites. */
export const PEERSONA_RESOURCES_MAX = ORGANIZATIONAL_RESOURCES_MAX;
/** @deprecated Prefer ORGANIZATIONAL_RESOURCES_VISIBLE. */
export const PEERSONA_RESOURCES_VISIBLE = ORGANIZATIONAL_RESOURCES_VISIBLE;

/**
 * @deprecated Ranking now uses the full transcript (KINDLY-1117). Kept so
 * older imports/tests that referenced the turn cap still resolve.
 */
export const PEERSONA_RESOURCES_CONTEXT_TURNS = Number.POSITIVE_INFINITY;

export interface PeersonaRankedResourcesResponse {
  resources: Resource[];
  ranked_at?: string;
}

type RankableMessage = {
  role: string;
  content?: string | null;
};

export interface OrganizationalResourcesRankRequest {
  id: number;
  context: string;
}

/** @deprecated Prefer OrganizationalResourcesRankRequest. */
export type PeersonaRankRequest = OrganizationalResourcesRankRequest;

/**
 * Deterministic ranking input from the full conversation transcript
 * (KINDLY-1117 — previously truncated to the last ~6 turns).
 */
export function buildResourcesRankingContext(messages: RankableMessage[]): string {
  const turns = messages.filter((m) => (m.role === 'user' || m.role === 'assistant') && (m.content ?? '').trim());
  return turns.map((m) => `${m.role}: ${(m.content ?? '').trim()}`).join('\n');
}

/** @deprecated Prefer buildResourcesRankingContext. */
export const buildPeersonaRankingContext = buildResourcesRankingContext;

async function rankOrganizationalResources(context: string): Promise<PeersonaRankedResourcesResponse> {
  const res = await axiosPostV3('/peersona/resources/rank', { context });
  return res.data;
}

/** Fallback static list when rank has not run or failed. */
export function useOrganizationalResourcesFallback() {
  const { user } = useAuth();
  const clientId = useSessionStorage('clientId') || user?.caller_role?.active_subscription?.package?.client?.id;
  const showGlobal = user?.caller_role?.active_subscription?.package?.client?.use_global_resources;

  return useResources({
    clientId: clientId ? Number(clientId) : undefined,
    showGlobal,
  });
}

/** @deprecated Prefer useOrganizationalResourcesFallback. */
export const usePeersonaResourcesFallback = useOrganizationalResourcesFallback;

export function useOrganizationalResourcesDisplay(
  rankRequest: OrganizationalResourcesRankRequest | null,
  panelOpen: boolean,
) {
  const fallback = useOrganizationalResourcesFallback();

  const rankQuery = useQuery({
    queryKey: ['organizationalResourcesRank', rankRequest?.id ?? 0],
    queryFn: () => rankOrganizationalResources(rankRequest!.context),
    enabled: panelOpen && rankRequest != null && rankRequest.id > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const ranked = (rankQuery.data?.resources ?? []).slice(0, ORGANIZATIONAL_RESOURCES_MAX);
  const fallbackResources = (fallback.data?.data ?? []).slice(0, ORGANIZATIONAL_RESOURCES_MAX);
  const resources = ranked.length > 0 ? ranked : fallbackResources;
  const isRanking = rankQuery.isFetching;
  const hasResources = resources.length > 0;
  const isLoading = panelOpen && isRanking && !hasResources;
  const isResorting = panelOpen && isRanking && hasResources;

  return {
    resources,
    isLoading: panelOpen && isLoading,
    isResorting: panelOpen && isResorting,
    isRanking,
    rankError: rankQuery.error,
    fallbackLoading: fallback.isLoading,
  };
}

/** @deprecated Prefer useOrganizationalResourcesDisplay. */
export const usePeersonaResourcesDisplay = useOrganizationalResourcesDisplay;

interface UseOrganizationalResourcesPanelArgs {
  messages: RankableMessage[];
  panelOpen: boolean;
}

/** Open/close, re-rank cadence, and display state for the resources drawer. */
export function useOrganizationalResourcesPanel({ messages, panelOpen }: UseOrganizationalResourcesPanelArgs) {
  const [open, setOpen] = useState(false);
  const [rankRequest, setRankRequest] = useState<OrganizationalResourcesRankRequest | null>(null);
  const lastRankedUserCountRef = useRef(0);
  const wasOpenRef = useRef(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const toggle = useCallback(() => setOpen((value) => !value), []);
  const close = useCallback(() => setOpen(false), []);
  const openDrawer = useCallback(() => setOpen(true), []);

  const requestRank = useCallback(() => {
    const context = buildResourcesRankingContext(messagesRef.current);
    setRankRequest((prev) => ({ id: (prev?.id ?? 0) + 1, context }));
  }, []);

  const userMessageCount = useMemo(
    () => messages.filter((m) => m.role === 'user' && (m.content ?? '').trim()).length,
    [messages],
  );

  const { resources, isLoading, isResorting } = useOrganizationalResourcesDisplay(rankRequest, open);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (justOpened) {
      lastRankedUserCountRef.current = userMessageCount;
      requestRank();
      return;
    }

    if (userMessageCount === 0) return;
    if (userMessageCount === lastRankedUserCountRef.current) return;
    lastRankedUserCountRef.current = userMessageCount;
    requestRank();
  }, [open, userMessageCount, requestRank]);

  useEffect(() => {
    if (!panelOpen && open) {
      setOpen(false);
      setRankRequest(null);
      lastRankedUserCountRef.current = 0;
      wasOpenRef.current = false;
    }
  }, [panelOpen, open]);

  return { open, toggle, close, openDrawer, resources, isLoading, isResorting };
}

/** Peersona welcome-hero resources strip. */
export function usePeersonaResourcesPanel() {
  const { messages, panelOpen } = usePeersona();
  return useOrganizationalResourcesPanel({ messages, panelOpen });
}

type FiToolLike = {
  tool?: string;
  result?: { items?: unknown[] };
};

/** True when an assistant turn carried a non-empty ``navigate_resources`` lane. */
function messageHasResourceLane(message: { role: string; timestamp?: string; tool_results?: FiToolLike[] }): boolean {
  if (message.role !== 'assistant') return false;
  return (message.tool_results ?? []).some(
    (t) => t.tool === 'navigate_resources' && Array.isArray(t.result?.items) && t.result.items.length > 0,
  );
}

/**
 * Fi resources strip (KINDLY-1117). Auto-opens the shared drawer when Fi
 * runs ``navigate_resources`` so results live in the drawer instead of an
 * in-chat carousel.
 */
export function useFiResourcesPanel() {
  const { messages, panelOpen } = useFi();
  const panel = useOrganizationalResourcesPanel({ messages, panelOpen });
  const lastAutoOpenedKeyRef = useRef<string | null>(null);
  const [highlightResources, setHighlightResources] = useState(false);

  useEffect(() => {
    if (!panelOpen) {
      lastAutoOpenedKeyRef.current = null;
      setHighlightResources(false);
      return;
    }

    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (!messageHasResourceLane(message)) continue;
      const key = `${i}-${message.timestamp ?? ''}`;
      if (lastAutoOpenedKeyRef.current === key) return;
      lastAutoOpenedKeyRef.current = key;
      panel.openDrawer();
      setHighlightResources(true);
      return;
    }
  }, [messages, panelOpen, panel.openDrawer]);

  useEffect(() => {
    if (panel.resources.length > 0) {
      setHighlightResources(true);
    }
  }, [panel.resources.length]);

  return { ...panel, highlightResources };
}
